import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MessageSquare, Send, Bug, Sparkles, Wrench, Clock, CheckCircle, AlertCircle, Loader2, Filter, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
  issue_resolution: { label: 'Issue', icon: Bug, color: 'bg-red-100 text-red-400 border-red-500/30' },
  new_feature: { label: 'Feature', icon: Sparkles, color: 'bg-violet-100 text-violet-400 border-violet-500/30' },
  feature_enhancement: { label: 'Enhancement', icon: Wrench, color: 'bg-amber-100 text-amber-400 border-amber-500/30' },
};

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-500/20 text-blue-400' },
  in_progress: { label: 'In Progress', color: 'bg-amber-500/20 text-amber-400' },
  resolved: { label: 'Resolved', color: 'bg-emerald-500/20 text-emerald-400' },
  closed: { label: 'Closed', color: 'bg-slate-500/20 text-slate-400' },
};

export default function SuperAdminSupport() {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const messagesEndRef = useRef(null);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });

  useEffect(() => { fetchTickets(); fetchStats(); }, [statusFilter, categoryFilter]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedTicket?.messages]);

  const fetchTickets = async () => {
    try {
      let url = `${API}/platform/support/tickets?`;
      if (statusFilter) url += `status=${statusFilter}&`;
      if (categoryFilter) url += `category=${categoryFilter}&`;
      const res = await axios.get(url, { headers: getHeaders() });
      setTickets(res.data);
      if (selectedTicket) {
        const updated = res.data.find(t => t.id === selectedTicket.id);
        if (updated) setSelectedTicket(updated);
      }
    } catch { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/platform/support/stats`, { headers: getHeaders() });
      setStats(res.data);
    } catch (err) { console.error("./pages/SuperAdminSupport.jsx:", err); }
  };

  const handleReply = async (newStatus) => {
    if (!replyText.trim() || !selectedTicket) return;
    setSending(true);
    try {
      await axios.post(`${API}/platform/support/tickets/${selectedTicket.id}/messages`,
        { message: replyText, status: newStatus || selectedTicket.status }, { headers: getHeaders() });
      setReplyText('');
      fetchTickets();
      fetchStats();
    } catch { toast.error('Failed to send reply'); }
    finally { setSending(false); }
  };

  const handleStatusChange = async (ticketId, status) => {
    try {
      await axios.put(`${API}/platform/support/tickets/${ticketId}/status`, { status }, { headers: getHeaders() });
      toast.success(`Ticket marked as ${status}`);
      fetchTickets();
      fetchStats();
    } catch { toast.error('Failed to update status'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" /></div>;

  return (
    <div data-testid="sa-support-page">
      {/* Stats */}
      <div className="flex items-center gap-4 mb-5">
        <h1 className="text-xl font-extrabold text-white flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <MessageSquare size={22} className="text-violet-400" />Tenant Support
        </h1>
        <div className="flex gap-2 ml-auto">
          {[
            { label: 'Open', value: stats.open || 0, color: 'text-blue-400' },
            { label: 'In Progress', value: stats.in_progress || 0, color: 'text-amber-400' },
            { label: 'Issues', value: stats.issues || 0, color: 'text-red-400' },
            { label: 'Features', value: stats.features || 0, color: 'text-violet-400' },
          ].map(s => (
            <div key={s.label} className="bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-1.5 text-center">
              <p className={`text-lg font-extrabold ${s.color}`}>{s.value}</p>
              <p className="text-[9px] text-slate-500 uppercase">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-4 h-[calc(100vh-200px)]">
        {/* Ticket List */}
        <div className="w-96 shrink-0 bg-slate-800/50 border border-slate-700 rounded-xl flex flex-col overflow-hidden">
          {/* Filters */}
          <div className="p-3 border-b border-slate-700 flex gap-2">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="flex-1 h-8 rounded bg-slate-900 border border-slate-600 text-white text-xs px-2" data-testid="filter-status">
              <option value="">All Status</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
              className="flex-1 h-8 rounded bg-slate-900 border border-slate-600 text-white text-xs px-2" data-testid="filter-category">
              <option value="">All Categories</option>
              <option value="issue_resolution">Issues</option>
              <option value="new_feature">Features</option>
              <option value="feature_enhancement">Enhancements</option>
            </select>
          </div>

          <div className="flex-1 overflow-y-auto">
            {tickets.length === 0 ? (
              <div className="text-center py-12 px-4">
                <MessageSquare size={28} className="mx-auto text-slate-600 mb-2" />
                <p className="text-xs text-slate-500">No tickets found</p>
              </div>
            ) : tickets.map(ticket => {
              const cat = CATEGORY_CONFIG[ticket.category] || CATEGORY_CONFIG.issue_resolution;
              const status = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
              const lastMsg = ticket.messages?.[ticket.messages.length - 1];
              const isUnread = lastMsg?.sender === 'tenant';
              return (
                <button key={ticket.id} onClick={() => setSelectedTicket(ticket)}
                  className={`w-full text-left p-3.5 border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors ${
                    selectedTicket?.id === ticket.id ? 'bg-slate-700/40 border-l-2 border-l-violet-500' : ''
                  }`} data-testid={`sa-ticket-${ticket.id}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Badge className={`text-[8px] font-bold border ${cat.color}`}>{cat.label}</Badge>
                    <Badge className={`text-[8px] font-bold ${status.color}`}>{status.label}</Badge>
                    {isUnread && <span className="w-2 h-2 rounded-full bg-blue-500 ml-auto" />}
                  </div>
                  <p className="text-sm font-semibold text-white truncate">{ticket.subject}</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <Building2 size={10} className="text-slate-500" />
                    <span className="text-[10px] text-slate-400">{ticket.tenant_name}</span>
                    <span className="text-[9px] text-slate-600 ml-auto">{ticket.updated_at ? new Date(ticket.updated_at).toLocaleDateString() : ''}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 bg-slate-800/50 border border-slate-700 rounded-xl flex flex-col overflow-hidden">
          {selectedTicket ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={`text-[8px] font-bold border ${CATEGORY_CONFIG[selectedTicket.category]?.color}`}>
                      {CATEGORY_CONFIG[selectedTicket.category]?.label}
                    </Badge>
                    <Badge className={`text-[8px] font-bold ${STATUS_CONFIG[selectedTicket.status]?.color}`}>
                      {STATUS_CONFIG[selectedTicket.status]?.label}
                    </Badge>
                  </div>
                  <h3 className="font-bold text-white text-sm">{selectedTicket.subject}</h3>
                  <p className="text-[10px] text-slate-500">
                    <Building2 size={10} className="inline mr-1" />{selectedTicket.tenant_name} | by {selectedTicket.created_by}
                  </p>
                </div>
                <div className="flex gap-1.5">
                  {['in_progress', 'resolved', 'closed'].map(s => (
                    <Button key={s} size="sm" variant="outline"
                      onClick={() => handleStatusChange(selectedTicket.id, s)}
                      disabled={selectedTicket.status === s}
                      className={`text-[10px] font-bold border-slate-600 ${selectedTicket.status === s ? 'opacity-50' : 'text-slate-300 hover:text-white hover:bg-slate-700'}`}
                      data-testid={`status-${s}`}>
                      {s === 'in_progress' ? 'In Progress' : s.charAt(0).toUpperCase() + s.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {(selectedTicket.messages || []).map(msg => (
                  <div key={msg.id} className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] rounded-xl px-4 py-2.5 ${
                      msg.sender === 'admin'
                        ? 'bg-violet-600 text-white rounded-br-sm'
                        : 'bg-slate-700 border border-slate-600 text-slate-200 rounded-bl-sm'
                    }`}>
                      <p className={`text-[10px] font-bold mb-0.5 ${msg.sender === 'admin' ? 'text-violet-300' : 'text-blue-400'}`}>
                        {msg.sender === 'admin' ? 'You (Admin)' : `${msg.sender_name} (${selectedTicket.tenant_name})`}
                      </p>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-[9px] mt-1 ${msg.sender === 'admin' ? 'text-violet-300' : 'text-slate-500'}`}>
                        {new Date(msg.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply */}
              <div className="p-3 border-t border-slate-700 flex gap-2">
                <Input value={replyText} onChange={e => setReplyText(e.target.value)}
                  placeholder="Type your reply..." className="flex-1 bg-slate-900 border-slate-600 text-white text-sm"
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply('in_progress'); } }}
                  data-testid="sa-reply-input" />
                <Button onClick={() => handleReply('in_progress')} disabled={sending || !replyText.trim()}
                  className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="sa-send-btn">
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare size={40} className="mx-auto text-slate-600 mb-3" />
                <p className="text-slate-400 font-semibold">Select a ticket to respond</p>
                <p className="text-xs text-slate-600 mt-1">View and reply to tenant support requests</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
